// frontend/src/services/metaPixel.js
//
// Meta Pixel - consent-gated, lazily injected, privacy-guarded.
//
// Contract (see .claude/skills/meta-ads/references/pixel-implementation.md):
//
// 1. NOTHING from Meta loads before the visitor grants MARKETING consent. The
//    fbevents.js script is injected on the first event after consent, never at
//    boot - loading connect.facebook.net pre-consent would already disclose the
//    visitor's IP/UA to Meta. `fbq('consent', ...)` is kept as belt-and-braces.
//
// 2. The pixel sends the full document location (`dl`) AND the referrer (`rl`)
//    with every event, and no parameter suppresses either. So every event is
//    refused - and the script is never injected - unless BOTH are safe to hand
//    to Meta (isSafeLocation + isSafeReferrer):
//      - no blocked path prefix (/supporter/* carries a confirm token, /admin is
//        internal, /profile/* is a CUSTOMER's site - their visitors, their
//        controller role, never ours to share);
//      - every query key on ALLOWED_QUERY_KEYS (our own attribution codes, Meta
//        / UTM click ids, harmless UI params). Anything else - `token=`,
//        `email=`, `winback=` (a signed, money-worth credential), a lead id -
//        drops the event. An allowLIST, not a denylist: a new sensitive param
//        must never reach Meta by default;
//      - a cross-origin referrer only when already trimmed to its origin (the
//        HYP payment page URL carries the payer's details; what HYP's referrer
//        policy leaves in it is not ours to decide).
//    The published privacy policy names a closed set of data types and promises
//    no name/email/phone; these checks are what keep that true in code.
//
// 3. PageView is fired MANUALLY, once per location. The pixel's own History-API
//    detection is switched off (`disablePushState`, undocumented - verify in
//    Events Manager Test Events) and autoConfig is off (no automatic button /
//    metadata events). `init` never receives user data.
//    NOT controllable from code: "Automatic Advanced Matching" is an Events
//    Manager toggle, ON by default for new pixels, that scrapes form fields
//    (email/phone/name) at event time. It MUST be switched off in Events
//    Manager before the pixel id is set - see the pre-spend verification in the
//    meta-ads skill reference. A policy promise depends on that console setting.
//
// 4. Tracks are held until fbevents.js has LOADED and are re-checked then: the
//    library reads `dl` when it drains its queue, not when we queue, so an event
//    queued on a safe URL must not be sent from wherever the user navigated to
//    in the meantime.
//
// 5. Events carry deterministic eventIDs so a future Conversions API addition is
//    a pure server change (dedup = event name + eventID):
//      CompleteRegistration / StartTrial -> cr-/st-<users.id>
//      Purchase                          -> pur-<transactions.id>
//
// With REACT_APP_META_PIXEL_ID unset the whole module is inert.

export const PIXEL_ID = String(process.env.REACT_APP_META_PIXEL_ID || '').trim();

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

// `/enterprise/pay` (#349): the pay-link token is IN THE PATH, and the pixel
// reports the full url as `dl`. The prefix blocks `/enterprise/pay/result` too -
// it carries nothing, but one prefix is simpler than an exception.
export const BLOCKED_PATH_PREFIXES = ['/supporter', '/admin', '/profile', '/enterprise/pay'];

export const ALLOWED_QUERY_KEYS = new Set([
  's', 'c', 'src',          // our own acquisition / campaign codes
  'fbclid',                 // Meta click id - needed for _fbc
  'tab', 'sort',            // UI state
  'order_id', 'status',     // /payment/success redirect (opaque gateway order id)
]);
const ALLOWED_QUERY_PREFIXES = ['utm_'];

// The only events this module will ever send. Anything else is a code change.
export const ALLOWED_EVENTS = new Set([
  'PageView',
  'CompleteRegistration',
  'StartTrial',
  'Purchase',
]);

let marketingGranted = false;
let loaded = false;
let scriptReady = false;
let scriptFailed = false;
let initialized = false;
let pending = [];
let lastPageViewHref = null;

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const isPrerender = () => hasWindow() && window.__PRERENDER__ === true;

const currentHref = () =>
  hasWindow() ? `${window.location.pathname}${window.location.search}${window.location.hash}` : '';

/**
 * Is it safe for Meta to see this location (it will, in full, as `dl`)?
 * Exported for tests.
 */
export const isSafeLocation = (loc) => {
  try {
    const location = loc || (hasWindow() ? window.location : null);
    if (!location) return false;

    // `//admin/x` is the same route as `/admin/x`.
    const path = String(location.pathname || '/').toLowerCase().replace(/\/{2,}/g, '/');
    const blocked = BLOCKED_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    );
    if (blocked) return false;

    const search = String(location.search || '');
    // Explicit, independent of the allowlist below: a token must never leave.
    if (/(^|[?&])[^=&]*token=/i.test(search)) return false;

    const params = new URLSearchParams(search);
    for (const key of params.keys()) {
      const k = key.toLowerCase();
      const allowed =
        ALLOWED_QUERY_KEYS.has(k) || ALLOWED_QUERY_PREFIXES.some((p) => k.startsWith(p));
      if (!allowed) return false;
    }

    // A fragment is part of `dl` too. Plain in-page anchors only.
    const hash = String(location.hash || '');
    if (hash && !/^#[A-Za-z0-9_-]*$/.test(hash)) return false;

    return true;
  } catch (error) {
    return false; // fail closed
  }
};

/**
 * Is it safe for Meta to see this referrer (sent as `rl` on every event)?
 * Empty -> safe. Same origin -> the same rules as the location. Cross origin ->
 * only when already trimmed to the bare origin (no path, query or fragment).
 * Exported for tests.
 */
export const isSafeReferrer = (referrer, origin) => {
  try {
    const ref = String(referrer == null ? '' : referrer);
    if (!ref) return true;
    const ownOrigin = origin || (hasWindow() ? window.location.origin : '');
    const url = new URL(ref);
    if (url.origin === ownOrigin) {
      return isSafeLocation({ pathname: url.pathname, search: url.search, hash: url.hash });
    }
    return (url.pathname === '/' || url.pathname === '') && !url.search && !url.hash;
  } catch (error) {
    return false; // unparseable -> fail closed
  }
};

const isSafeContext = () =>
  isSafeLocation() && isSafeReferrer(hasWindow() ? document.referrer : '');

// Standard fbq queue stub (the body of Meta's base snippet, minus its automatic
// PageView). Calls queue until fbevents.js arrives and drains them.
const installStub = () => {
  if (window.fbq) return;
  const fbq = function fbqStub(...args) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  // Guard (b): no automatic PageView on SPA history changes - we fire our own.
  fbq.disablePushState = true;
  fbq.allowDuplicatePageViews = false;
  window.fbq = fbq;
};

const dispatch = ({ eventName, params, eventId }) => {
  try {
    if (eventId) {
      window.fbq('track', eventName, params || {}, { eventID: eventId });
    } else {
      window.fbq('track', eventName, params || {});
    }
    return true;
  } catch (error) {
    return false;
  }
};

// `init` is deferred until fbevents.js has loaded AND consent still holds: a
// revoke inside the load window must not let the library start (its config
// fetch and _fbp write) after the visitor already said no.
const ensureInit = () => {
  if (initialized || !marketingGranted) return initialized;
  // No second argument: no manual advanced matching. (Automatic advanced
  // matching is an Events Manager toggle - see point 3 above.)
  window.fbq('init', PIXEL_ID);
  initialized = true;
  return true;
};

// Runs when fbevents.js has loaded. Each held event is sent only if the user is
// still on the location it was queued for, and that location is still safe.
const flushPending = () => {
  scriptReady = true;
  ensureInit();
  const held = pending;
  pending = [];
  held.forEach((entry) => {
    if (!marketingGranted) return;
    if (entry.href !== currentHref() || !isSafeContext()) return;
    dispatch(entry);
  });
};

const ensureLoaded = () => {
  if (loaded) return true;
  if (!PIXEL_ID || !marketingGranted || isPrerender() || !hasWindow()) return false;
  if (!isSafeContext()) return false; // guard (a): never INITIALIZE on an unsafe URL

  installStub();
  window.fbq('consent', 'grant');
  window.fbq('set', 'autoConfig', false, PIXEL_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  script.onload = flushPending;
  script.onerror = () => {
    // Blocked (ad blocker) or offline: stop queuing for the rest of the page.
    scriptFailed = true;
    pending = [];
  };
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    (document.head || document.body).appendChild(script);
  }

  loaded = true;
  return true;
};

/**
 * Called from consentUtils.applyConsentPreferences - the single place a
 * consent decision (stored or fresh) is applied.
 */
export const setMarketingConsent = (granted) => {
  const next = granted === true;
  const wasGranted = marketingGranted;
  marketingGranted = next;

  if (!next) {
    // Held events are discarded, so the PageView they carried was never sent -
    // a later re-grant on the same page must be free to count it.
    pending = [];
    lastPageViewHref = null;
  }

  if (loaded) {
    try {
      window.fbq('consent', next ? 'grant' : 'revoke');
    } catch (error) {
      /* pixel failures must never break the app */
    }
  }
  // First grant in this page: count the page the visitor is on.
  if (next && !wasGranted) trackPageView();
};

const send = (eventName, params, eventId) => {
  if (!ALLOWED_EVENTS.has(eventName)) return false;
  if (!PIXEL_ID || !marketingGranted || isPrerender()) return false;
  if (!isSafeContext()) return false;
  if (scriptFailed || !ensureLoaded()) return false;
  const entry = { eventName, params, eventId, href: currentHref() };
  if (!scriptReady) {
    pending.push(entry);
    return true;
  }
  if (!ensureInit()) return false;
  return dispatch(entry);
};

/** One PageView per distinct location (consent grant + route effect can both ask). */
export const trackPageView = () => {
  if (!hasWindow() || !marketingGranted) return false;
  const href = `${window.location.pathname}${window.location.search}`;
  if (href === lastPageViewHref) return false;
  // Recorded even when refused, so a return from an unsafe page to the previous
  // safe one is still counted as a new view.
  lastPageViewHref = href;
  return send('PageView');
};

// Ids are opaque server ids. Anything else is refused rather than sent.
const safeId = (value) => {
  const id = String(value == null ? '' : value).trim();
  return /^[A-Za-z0-9-]{1,64}$/.test(id) ? id : null;
};

export const trackCompleteRegistration = (userId) => {
  const id = safeId(userId);
  return id ? send('CompleteRegistration', {}, `cr-${id}`) : false;
};

export const trackStartTrial = (userId) => {
  const id = safeId(userId);
  return id ? send('StartTrial', {}, `st-${id}`) : false;
};

export const trackPurchase = (transactionId, value) => {
  const id = safeId(transactionId);
  const amount = Number(value);
  if (!id || !Number.isFinite(amount) || amount <= 0) return false;
  return send('Purchase', { value: amount, currency: 'ILS' }, `pur-${id}`);
};

// The verify response carries no user id, so the id minted at registration is
// handed to the verify page through sessionStorage (same browser only - which is
// the only case where a pixel event could match the ad click anyway).
const REGISTERED_ID_KEY = 'lf_px_reg_uid';

export const rememberRegisteredUserId = (userId) => {
  const id = safeId(userId);
  if (!id) return;
  try {
    sessionStorage.setItem(REGISTERED_ID_KEY, id);
  } catch (error) {
    /* storage blocked - StartTrial simply does not fire */
  }
};

export const consumeRegisteredUserId = () => {
  try {
    const id = sessionStorage.getItem(REGISTERED_ID_KEY);
    sessionStorage.removeItem(REGISTERED_ID_KEY);
    return safeId(id);
  } catch (error) {
    return null;
  }
};

/** Test-only: reset module state between cases. */
export const __resetForTests = () => {
  marketingGranted = false;
  loaded = false;
  scriptReady = false;
  scriptFailed = false;
  initialized = false;
  pending = [];
  lastPageViewHref = null;
};

/** Test-only: simulate fbevents.js finishing loading. */
export const __simulateScriptLoadForTests = () => flushPending();
